import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../service/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshFailed = false; // Prevent infinite refresh loops after a definitive failure
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService
  ) {}

  // Auth endpoints must never trigger the refresh retry — they handle their own errors
  private isAuthEndpoint(url: string): boolean {
    return url.includes('auth/login') ||
           url.includes('auth/logout') ||
           url.includes('auth/refresh');
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Add withCredentials so HttpOnly cookies are sent automatically.
    // Add X-Requested-With for CSRF protection (TASK-1.10).
    const clonedRequest = request.clone({
      withCredentials: true,
      setHeaders: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(request.url)) {
          // If refresh already failed this session, propagate directly — don't retry
          if (this.refreshFailed) {
            return throwError(() => error);
          }
          // TASK-1.11: Try to refresh the token before logging out
          return this.handleTokenRefresh(clonedRequest, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      // Another refresh is in progress — wait for it, then retry once
      return this.refreshDone$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => next.handle(request))
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(false);

    return this.authService.refreshToken().pipe(
      switchMap(() => {
        this.isRefreshing = false;
        this.refreshFailed = false; // Refresh succeeded — reset failure flag
        this.refreshDone$.next(true);
        // Retry original request — pipe through next directly, NOT through intercept,
        // to avoid re-entering the refresh cycle if the retry itself gets 401.
        return next.handle(request).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // Retry after successful refresh still 401 — propagate to component.
            // Do NOT navigate away; let the component decide how to handle it.
            return throwError(() => retryError);
          })
        );
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        // Mark refresh as failed to prevent subsequent requests from triggering
        // another refresh attempt (which would fail again with the same revoked token).
        this.refreshFailed = true;
        // Propagate the original error to the component — block only that page,
        // NOT the entire session. Do NOT call navigateToLogin() here.
        return throwError(() => originalError);
      })
    );
  }
}
